import React, { useState } from 'react';
import { useFlag } from '@reflag/react-sdk';
import {
  FormControl,
  MenuItem,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  styled,
  TextField,
} from '@mui/material';
import Heading from 'components/Heading';
import PageLoader from 'components/PageLoader';
import Spacing from 'components/Spacing';
import Button from 'components/Button';
import { Row } from 'components/Layout';
import api from 'lib/trpcClient';
import { useLayoutContext } from 'contexts/LayoutContext';
import { CalendarDay, numberToCurrencyString } from 'further-ui/utils';
import downloadXlxs from 'utils/downloadXlxs';
import useVehicle from 'hooks/lp/data/vehicle/useVehicle';
import useHandleTrpcMutation from 'hooks/ui/useHandleTrpcMutation';
import {
  TrialBalanceDateOption,
  TrialBalanceGroupByOption,
} from 'further-types/lp/general-ledger';
import createTrialBalanceRow from './createTrialBalanceRow';

const StyledTableContainer = styled(TableContainer)(({ theme }) => ({
  marginTop: theme.spacing(2),
  border: `1px solid ${theme.palette.divider}`,
}));

const StyledTableHead = styled(TableHead)(({ theme }) => ({
  backgroundColor: theme.palette.background.default,
}));

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  padding: theme.spacing(1.5, 2),
  fontSize: '0.875rem',
  fontWeight: 500,
}));

const BodyTableCell = styled(TableCell)(({ theme }) => ({
  padding: theme.spacing(1.5, 2),
  fontSize: '0.875rem',
}));

const TotalRowCell = styled(TableCell)(({ theme }) => ({
  padding: theme.spacing(1.5, 2),
  fontSize: '0.875rem',
  fontWeight: 600,
  backgroundColor: theme.palette.action.hover,
}));

const TrialBalance: React.FC = () => {
  const { isEnabled: showOtherAccounting } = useFlag('other-accounting');
  const { selectedVehicleId } = useLayoutContext();
  const { vehicle } = useVehicle(selectedVehicleId);
  // TODO (Petru): refactor to use useFilters hook
  const [dateOption, setDateOption] = useState<TrialBalanceDateOption>(
    TrialBalanceDateOption.EndLastQuarter,
  );
  const [customDate, setCustomDate] = useState<string>('');
  const [groupBy, setGroupBy] = useState<TrialBalanceGroupByOption>(
    TrialBalanceGroupByOption.GlAccount,
  );

  const { data, isLoading } = api.generalLedger.trialBalance.useQuery(
    {
      vehicleId: selectedVehicleId ?? '',
      dateOption,
      customDate:
        dateOption === TrialBalanceDateOption.Custom && customDate
          ? new CalendarDay(customDate)
          : undefined,
      groupBy,
    },
    {
      enabled:
        showOtherAccounting &&
        !!selectedVehicleId &&
        !(dateOption === TrialBalanceDateOption.Custom && !customDate),
    },
  );

  const getHandleTrpcMutation = useHandleTrpcMutation<{ file: string }>();
  const exportTrialBalance = api.generalLedger.exportTrialBalance.useMutation(
    getHandleTrpcMutation({
      onSuccess: (responseData) => {
        const dateLabel = data?.periodEndDate?.toReadable() ?? 'Unknown date';

        downloadXlxs(
          responseData.file,
          `Trial balance - ${vehicle?.vehicleName} - ${dateLabel}`,
        );
      },
    }),
  );

  if (!showOtherAccounting) {
    return null;
  }

  const handleExport = () => {
    if (dateOption === TrialBalanceDateOption.Custom && !customDate) {
      return;
    }
    exportTrialBalance.mutate({
      vehicleId: selectedVehicleId ?? '',
      dateOption,
      customDate:
        dateOption === TrialBalanceDateOption.Custom && customDate
          ? new CalendarDay(customDate)
          : undefined,
      groupBy,
    });
  };

  if (isLoading || !selectedVehicleId) {
    return <PageLoader />;
  }

  const showDatePicker = dateOption === TrialBalanceDateOption.Custom;
  const rows = data?.rows ?? [];
  const yearEndDate = data?.periodEndDate?.toReadable() ?? '-';

  const totalRow = createTrialBalanceRow(rows);

  const columnCount =
    (groupBy === TrialBalanceGroupByOption.GlAccount ? 1 : 0) + // Code column
    2 + // Account name + Account type
    5; // 5 debit/credit columns (starting balance, debits, credits, changes, balance)

  return (
    <Spacing>
      <Heading variant="h3" noMargin>
        Trial balance
      </Heading>

      <Row justify="space-between" spacing="xs" centered>
        <Row spacing="xs" centered fullWidth={false}>
          <FormControl size="small" sx={{ minWidth: 200 }}>
            <Select
              value={dateOption}
              onChange={(e) =>
                setDateOption(e.target.value as TrialBalanceDateOption)
              }
              label="Date"
            >
              <MenuItem value={TrialBalanceDateOption.Today}>Today</MenuItem>
              <MenuItem value={TrialBalanceDateOption.EndThisMonth}>
                End of this month
              </MenuItem>
              <MenuItem value={TrialBalanceDateOption.EndLastMonth}>
                End of last month
              </MenuItem>
              <MenuItem value={TrialBalanceDateOption.EndLastQuarter}>
                End of last quarter
              </MenuItem>
              <MenuItem value={TrialBalanceDateOption.EndLastFinancialYear}>
                End of last financial year
              </MenuItem>
              <MenuItem value={TrialBalanceDateOption.Custom}>Custom</MenuItem>
            </Select>
          </FormControl>

          {showDatePicker && (
            <TextField
              type="date"
              value={customDate}
              onChange={(e) => setCustomDate(e.target.value)}
              size="small"
              InputLabelProps={{ shrink: true }}
            />
          )}

          <FormControl size="small" sx={{ minWidth: 150 }}>
            <Select
              value={groupBy}
              onChange={(e) =>
                setGroupBy(e.target.value as TrialBalanceGroupByOption)
              }
              label="Group by"
            >
              <MenuItem value={TrialBalanceGroupByOption.GlAccount}>
                GL account
              </MenuItem>
              <MenuItem value={TrialBalanceGroupByOption.TransactionType}>
                Transaction type
              </MenuItem>
            </Select>
          </FormControl>
        </Row>

        <Button
          variant="contained"
          onClick={handleExport}
          loading={exportTrialBalance.isPending}
        >
          Export
        </Button>
      </Row>

      <StyledTableContainer>
        <Table>
          <StyledTableHead>
            <TableRow>
              {groupBy === TrialBalanceGroupByOption.GlAccount && (
                <StyledTableCell align="left">Account code</StyledTableCell>
              )}
              <StyledTableCell align="left">Account</StyledTableCell>
              <StyledTableCell align="left">Account type</StyledTableCell>
              <StyledTableCell align="right">Starting balance</StyledTableCell>
              <StyledTableCell align="right">
                Debits - year to date
              </StyledTableCell>
              <StyledTableCell align="right">
                Credits - year to date
              </StyledTableCell>
              <StyledTableCell align="right">Changes</StyledTableCell>
              <StyledTableCell align="right">
                Balance - {yearEndDate}
              </StyledTableCell>
            </TableRow>
          </StyledTableHead>
          <TableBody>
            {rows.length === 0 ? (
              <TableRow>
                <BodyTableCell colSpan={columnCount} align="center">
                  No data
                </BodyTableCell>
              </TableRow>
            ) : (
              <>
                {rows.map((row, idx) => (
                  <TableRow key={idx}>
                    {groupBy === TrialBalanceGroupByOption.GlAccount && (
                      <BodyTableCell align="left">
                        {row.code || '-'}
                      </BodyTableCell>
                    )}
                    <BodyTableCell align="left">
                      {row.accountName}
                    </BodyTableCell>
                    <BodyTableCell align="left">
                      {row.accountType || '-'}
                    </BodyTableCell>
                    <BodyTableCell align="right">
                      {numberToCurrencyString(row.startingBalance, {
                        currency: vehicle?.baseCurrency,
                      })}
                    </BodyTableCell>
                    <BodyTableCell align="right">
                      {numberToCurrencyString(row.debitsYtd, {
                        currency: vehicle?.baseCurrency,
                      })}
                    </BodyTableCell>
                    <BodyTableCell align="right">
                      {numberToCurrencyString(row.creditsYtd, {
                        currency: vehicle?.baseCurrency,
                      })}
                    </BodyTableCell>
                    <BodyTableCell align="right">
                      {numberToCurrencyString(row.change, {
                        currency: vehicle?.baseCurrency,
                        negativeInBrackets: true,
                      })}
                    </BodyTableCell>
                    <BodyTableCell align="right">
                      {numberToCurrencyString(row.balance, {
                        currency: vehicle?.baseCurrency,
                      })}
                    </BodyTableCell>
                  </TableRow>
                ))}
                <TableRow>
                  {groupBy === TrialBalanceGroupByOption.GlAccount && (
                    <TotalRowCell align="left" />
                  )}
                  <TotalRowCell align="left">Total</TotalRowCell>
                  <TotalRowCell align="left" />
                  <TotalRowCell align="right" />
                  <TotalRowCell align="right">
                    {numberToCurrencyString(totalRow.debitsYtd, {
                      currency: vehicle?.baseCurrency,
                    })}
                  </TotalRowCell>
                  <TotalRowCell align="right">
                    {numberToCurrencyString(totalRow.creditsYtd, {
                      currency: vehicle?.baseCurrency,
                    })}
                  </TotalRowCell>
                  <TotalRowCell align="right" />
                  <TotalRowCell align="right" />
                </TableRow>
              </>
            )}
          </TableBody>
        </Table>
      </StyledTableContainer>
    </Spacing>
  );
};

export default TrialBalance;
